import { differenceInCalendarDays, format, formatDistanceToNow, isSameDay } from "date-fns";
import { es } from "date-fns/locale";
import apiClient from "@/lib/api-client";

/**
 * Piezas COMPARTIDAS entre el inbox de WhatsApp del inquilino
 * (`pages/Conversations.tsx`, contra `/api/whatsapp-conversations`) y el
 * inbox de soporte de plataforma (`pages/admin/PlatformSupport.tsx`, contra
 * `/api/admin/platform-conversations`). Ambos endpoints son un calco a
 * propósito (ver `Documentos/PLAN_WHATSAPP_IA.md`), pero el shape de la FILA
 * de la lista difiere (uno tiene `locationId/locationName`, el otro
 * `matched*`) — por eso solo se comparte lo que es IDÉNTICO en los dos
 * contratos: modo, mensajes y las acciones de handoff.
 */

export type ConversationMode = "bot" | "human" | "closed";
export type ConversationModeFilter = "" | ConversationMode;
export type SenderType = "customer" | "bot" | "staff";

export interface ConversationMessageItem {
  id: string;
  direction: "in" | "out";
  senderType: SenderType;
  senderUserId: string | null;
  body: string;
  messageType: string;
  /** Media id de Meta si el mensaje entrante trae adjunto (audio, sobre todo):
   *  con él la burbuja ofrece "Escuchar audio" contra /:id/media/:messageId. */
  mediaId?: string | null;
  createdAt: string;
  /** Solo local: el envío devolvió 502 (Meta lo rechazó) pero el mensaje sí se guardó. */
  notDelivered?: boolean;
}

/** Shape común de `POST /:id/take-over` · `/:id/return-to-bot` · `/:id/close`. */
export interface ConversationActionState {
  id: string;
  mode: ConversationMode;
  assignedUserId: string | null;
  handoffReason: string | null;
  handoffAt: string | null;
  unreadCount: number;
  lastMessageAt: string | null;
  lastCustomerMessageAt: string | null;
  canReplyFreeform: boolean;
}

/** Shape común de `POST /:id/messages` en 200 (y también del `data` que
 *  viaja en el 502 cuando Meta rechaza el envío pero el mensaje SÍ se
 *  guardó — mismo shape, distinto código HTTP). */
export interface ConversationSendMessagePayload {
  message: ConversationMessageItem;
  conversation: {
    id: string;
    mode: ConversationMode;
    assignedUserId: string | null;
  };
}

export interface ConversationSendMessageErrorBody {
  success: false;
  message: string;
  data?: ConversationSendMessagePayload;
}

export type ConversationActionName =
  | "take-over"
  | "return-to-bot"
  | "answer-with-ai"
  | "close";

export const CONVERSATION_ACTION_ERROR_FALLBACK: Record<
  ConversationActionName,
  string
> = {
  "take-over": "No se pudo tomar el control de la conversación",
  "return-to-bot": "No se pudo devolver la conversación al asistente",
  "answer-with-ai": "La IA no pudo responder ahora mismo",
  close: "No se pudo cerrar la conversación",
};

export const CONVERSATION_PAGE_SIZE = 20;
export const CONVERSATION_LIST_POLL_MS = 30000;
export const CONVERSATION_DETAIL_POLL_MS = 15000;

export function formatWaId(waId: string): string {
  return waId ? `+${waId}` : waId;
}

/** Canal de una conversación del inbox. En Instagram y Messenger `waId` NO es
 *  un teléfono: es un id de Meta, y quien escribe se enseña por su nombre. */
export type ConversationChannel = "whatsapp" | "instagram" | "messenger" | "web";

/**
 * Cómo se enseña cada canal que no es WhatsApp: su nombre y su color (rosa
 * Instagram, azul Facebook), para que en una lista mezclada se distingan de
 * un vistazo del verde de WhatsApp. Messenger se llama "Facebook", que es como
 * lo llama quien atiende el negocio.
 *
 * Las clases van escritas enteras a propósito: Tailwind solo genera las que
 * encuentra tal cual en el código.
 */
export interface SocialChannelStyle {
  label: string;
  /** Etiqueta con el nombre del canal. */
  chip: string;
  /** Franja lateral de la fila en una lista. Es una sombra interior y no un
   *  borde: las listas separan sus filas con `divide-y`, que manda sobre el
   *  color de cualquier borde de la fila. */
  stripe: string;
  /** Contador de mensajes sin leer. */
  counter: string;
  /** Cabecera de la conversación abierta, y sus botones al pasar el ratón. */
  header: string;
  headerHover: string;
  /** Icono suelto (campana de avisos). */
  icon: string;
}

const SOCIAL_CHANNEL_STYLE: Partial<Record<ConversationChannel, SocialChannelStyle>> = {
  instagram: {
    label: "Instagram",
    chip: "bg-pink-50 text-pink-700",
    stripe: "shadow-[inset_4px_0_0_0_#f472b6]",
    counter: "bg-pink-500",
    header: "bg-pink-600",
    headerHover: "hover:bg-pink-500",
    icon: "text-pink-500",
  },
  messenger: {
    label: "Facebook",
    chip: "bg-blue-50 text-blue-700",
    stripe: "shadow-[inset_4px_0_0_0_#60a5fa]",
    counter: "bg-blue-500",
    header: "bg-blue-600",
    headerHover: "hover:bg-blue-500",
    icon: "text-blue-500",
  },
};

/** Nombre y colores del canal, o null si la conversación es de WhatsApp. */
export function socialChannelStyle(
  channel?: ConversationChannel | string | null,
): SocialChannelStyle | null {
  return (channel && SOCIAL_CHANNEL_STYLE[channel as ConversationChannel]) || null;
}

/** "Instagram" / "Facebook", o null si la conversación es de WhatsApp. */
export function socialChannelLabel(channel?: ConversationChannel | null): string | null {
  return socialChannelStyle(channel)?.label ?? null;
}

export function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es });
  } catch {
    return "—";
  }
}

/** Hora del mensaje dentro de la burbuja, como en WhatsApp («14:32»). */
export function messageTime(iso: string): string {
  return format(new Date(iso), "HH:mm", { locale: es });
}

/** Fecha y hora completas, para el `title` de la hora (al pasar el ratón). */
export function messageFullDate(iso: string): string {
  return format(new Date(iso), "EEEE d 'de' MMMM 'de' yyyy, HH:mm", { locale: es });
}

/** ¿Este mensaje es el primero de su día? Entonces lleva separador de fecha
 *  encima, como en WhatsApp. Se compara el día en la hora del navegador. */
export function startsNewDay(iso: string, previousIso: string | undefined): boolean {
  return !previousIso || !isSameDay(new Date(iso), new Date(previousIso));
}

/** Texto del separador de día: «Hoy», «Ayer», el día de la semana si es de
 *  los últimos 7 días, y si no la fecha («12 de octubre», con año si no es
 *  el actual). */
export function messageDayLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const daysAgo = differenceInCalendarDays(now, date);
  if (daysAgo === 0) return "Hoy";
  if (daysAgo === 1) return "Ayer";
  const label =
    daysAgo > 1 && daysAgo < 7
      ? format(date, "EEEE", { locale: es })
      : format(date, date.getFullYear() === now.getFullYear() ? "d 'de' MMMM" : "d 'de' MMMM 'de' yyyy", {
          locale: es,
        });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function lastMessagePrefix(senderType: SenderType): string {
  if (senderType === "bot") return "IA: ";
  if (senderType === "staff") return "Tú: ";
  return "";
}

/** Solo necesita los dos campos de asignación, no el tipo de detalle completo
 *  — así vale tanto para `ConversationDetailData` (inquilino) como para el
 *  tipo de detalle del inbox de plataforma. */
export function resolveStaffLabel(
  message: ConversationMessageItem,
  detail: { assignedUserId: string | null; assignedUserName: string | null },
  authUserId?: string,
): string {
  if (message.senderUserId && message.senderUserId === authUserId) return "Tú";
  if (
    message.senderUserId &&
    detail.assignedUserId &&
    message.senderUserId === detail.assignedUserId &&
    detail.assignedUserName
  ) {
    return detail.assignedUserName;
  }
  return "Un compañero";
}

export function getConversationErrorResponse(
  err: unknown,
): { status?: number; data?: unknown } | undefined {
  return (err as { response?: { status?: number; data?: unknown } })
    ?.response;
}

// ─── Adjuntos: fotos, vídeos, audios y documentos (22-sep-2026) ─────────────

export type ConversationMediaKind = "image" | "video" | "audio" | "document";

const MB = 1024 * 1024;

/** Formatos que WhatsApp admite para enviar, con su tope (mismos límites que
 *  el backend, `whatsapp/media-formats.ts`). En el iPhone, pedir JPG hace que
 *  Safari convierta las fotos HEIC solo; los vídeos .mov no se admiten. */
const CONVERSATION_MEDIA: Record<string, { kind: ConversationMediaKind; maxBytes: number }> = {
  "image/jpeg": { kind: "image", maxBytes: 5 * MB },
  "image/png": { kind: "image", maxBytes: 5 * MB },
  "video/mp4": { kind: "video", maxBytes: 16 * MB },
  "video/3gpp": { kind: "video", maxBytes: 16 * MB },
  "audio/aac": { kind: "audio", maxBytes: 16 * MB },
  "audio/mp4": { kind: "audio", maxBytes: 16 * MB },
  "audio/mpeg": { kind: "audio", maxBytes: 16 * MB },
  "audio/amr": { kind: "audio", maxBytes: 16 * MB },
  "audio/ogg": { kind: "audio", maxBytes: 16 * MB },
  "application/pdf": { kind: "document", maxBytes: 100 * MB },
  "text/plain": { kind: "document", maxBytes: 100 * MB },
  "application/msword": { kind: "document", maxBytes: 100 * MB },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    kind: "document",
    maxBytes: 100 * MB,
  },
  "application/vnd.ms-excel": { kind: "document", maxBytes: 100 * MB },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    kind: "document",
    maxBytes: 100 * MB,
  },
  "application/vnd.ms-powerpoint": { kind: "document", maxBytes: 100 * MB },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": {
    kind: "document",
    maxBytes: 100 * MB,
  },
};

/** Tipos con los que el navegador etiqueta algunos archivos y que Meta
 *  espera con otro nombre (un .m4a llega como audio/x-m4a). */
const MEDIA_TYPE_ALIASES: Record<string, string> = {
  "audio/x-m4a": "audio/mp4",
  "audio/mp3": "audio/mpeg",
};

export const CONVERSATION_MEDIA_ACCEPT = [
  ...Object.keys(CONVERSATION_MEDIA),
  ...Object.keys(MEDIA_TYPE_ALIASES),
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.mp3,.m4a,.ogg,.aac",
].join(",");

const MEDIA_FORMATS_HELP =
  "fotos (JPG, PNG), vídeos (MP4), audios (MP3, M4A, OGG, AAC) y documentos (PDF, Word, Excel, PowerPoint, TXT)";

/** Tipo con el que se manda el archivo (resolviendo los alias del navegador). */
export function conversationMediaType(file: File): string {
  return MEDIA_TYPE_ALIASES[file.type] ?? file.type;
}

export function conversationMediaKind(file: File): ConversationMediaKind | null {
  return CONVERSATION_MEDIA[conversationMediaType(file)]?.kind ?? null;
}

/** `null` si el archivo se puede enviar; si no, el motivo para el toast. */
export function validateConversationMediaFile(file: File): string | null {
  const spec = CONVERSATION_MEDIA[conversationMediaType(file)];
  if (!spec) {
    return `Formato no admitido: WhatsApp acepta ${MEDIA_FORMATS_HELP}.`;
  }
  if (file.size > spec.maxBytes) {
    return `El archivo es demasiado grande: WhatsApp admite hasta ${spec.maxBytes / MB} MB para este tipo.`;
  }
  return null;
}

/** Interpreta el cuerpo con el que el backend guarda un adjunto
 *  (`attachmentBody` en `whatsapp/media-formats.ts`): "📎 nombre.pdf" y, en
 *  la línea siguiente, el texto que lo acompaña; "📷 Foto" / "🎥 Vídeo" /
 *  "🎤 Audio" cuando no hay texto; "📷 Imagen: …" es la descripción de la IA
 *  de una foto entrante; "[mensaje de tipo …]" un adjunto sin procesar.
 *  Cualquier otro cuerpo es el texto que acompaña a una foto o vídeo. */
export function parseAttachmentBody(body: string): {
  filename: string | null;
  caption: string | null;
  description: string | null;
} {
  if (body.startsWith("📎 ")) {
    const newline = body.indexOf("\n");
    const filename = (newline === -1 ? body.slice(2) : body.slice(2, newline)).trim() || null;
    const caption = newline === -1 ? null : body.slice(newline + 1).trim() || null;
    return { filename, caption, description: null };
  }
  if (body.startsWith("📷 Imagen:")) return { filename: null, caption: null, description: body };
  if (/^(📷 Foto|🎥 Vídeo|🎤 Audio)$|^\[mensaje de tipo /u.test(body)) {
    return { filename: null, caption: null, description: null };
  }
  return { filename: null, caption: body, description: null };
}

/** Sube un adjunto a `POST <conversationPath>/media`: el archivo viaja crudo
 *  (Content-Type = su tipo), el texto como `?caption=` y, en documentos, el
 *  nombre como `?filename=`. Responde con el mismo shape que
 *  `POST /:id/messages` (también el 502 con `data`). */
export function sendConversationMedia(conversationPath: string, file: File, caption: string) {
  const params: Record<string, string> = {};
  if (caption) params.caption = caption;
  if (conversationMediaKind(file) === "document") params.filename = file.name;
  return apiClient.post<{
    success: true;
    message: string;
    data: ConversationSendMessagePayload;
  }>(`${conversationPath}/media`, file, {
    headers: { "Content-Type": conversationMediaType(file) },
    params: Object.keys(params).length > 0 ? params : undefined,
  });
}
